import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { useApolloClient, useQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { Button } from '@repo/shared-client/components/ui'
import { useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import SFPageLoading from 'src/components/primatives/sf-page-loading/sf-page-loading'
import SFSpinner from 'src/components/primatives/sf-spinner/sf-spinner'
import ReviewCard from 'src/features/review/review-card/review-card'
import { ReviewInfo } from 'src/lib/fragments'
import { useTranslation } from 'src/lib/intl/react'
import type { ReviewsQuery, ReviewsQueryVariables } from 'types/graphql'
import ReviewCreateEditDialog from '../review-create-edit-dialog/review-create-edit-dialog'

export const REVIEWS_QUERY: TypedDocumentNode<ReviewsQuery, ReviewsQueryVariables> = gql`
  query ReviewsQuery($filter: ReviewFilter!, $cursor: String) {
    reviews: reviewsCursor(filter: $filter, cursor: $cursor) {
      cursor
      count
      entries {
        ...ReviewInfo
        subReply {
          ...ReviewInfo
        }
      }
    }
  }
  ${ReviewInfo}
`

type ReviewsProps = {
  placeIdOrSlug?: string
  placeOwnerId?: string
  publicationIdOrSlug?: string
  publicationOwnerId?: string
  vendorMarketIdOrSlug?: string
  vendorMarketItemIdOrSlug?: string
  vendorMarketOwnerId?: string
  profileId?: string
  scrollableId?: string
  cursor?: string
  canReview?: boolean
  onReviewChange?: (data: object) => void
  title?: string
}

const Reviews = ({
  placeIdOrSlug,
  publicationIdOrSlug,
  placeOwnerId,
  publicationOwnerId,
  vendorMarketIdOrSlug,
  vendorMarketItemIdOrSlug,
  vendorMarketOwnerId,
  profileId,
  cursor,
  canReview,
  onReviewChange,
}: ReviewsProps) => {
  const { t } = useTranslation()
  const filter = {
    placeIdOrSlug: placeIdOrSlug ?? undefined,
    publicationIdOrSlug: publicationIdOrSlug ?? undefined,
    vendorMarketIdOrSlug: vendorMarketIdOrSlug ?? undefined,
    vendorMarketItemIdOrSlug: vendorMarketItemIdOrSlug ?? undefined,
  }

  const queryResult = useQuery(REVIEWS_QUERY, {
    variables: {
      filter,
      cursor,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const { data, error, networkStatus } = queryResult
  const { cache } = useApolloClient()
  const [reviewModal, setReviewModal] = useState(false)

  const handleRefreshData = () => {
    queryResult.refetch()
  }

  const handleEditReview = (data: object) => {
    const d = data as { id: string; content: string; rating: number; ownerId?: string }
    const id = cache.identify({ __typename: 'Review', id: d.id })
    cache.modify({
      id: id,
      fields: {
        content: () => d.content,
        rating: () => d.rating,
        ownerId: () => d.ownerId,
      },
    })
  }

  const handleDeleteReview = (id: string) => {
    const ident = cache.identify({ __typename: 'Review', id: id })
    cache.evict({ id: ident })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  const reviews = data?.reviews

  return (
    <InfiniteScroll
      dataLength={reviews?.entries?.length ?? 0}
      hasMore={reviews?.cursor !== null && reviews?.cursor !== undefined}
      loader={
        <div className="flex justify-center py-4">
          <SFSpinner size="sm" />
        </div>
      }
      next={() =>
        queryResult.fetchMore({
          variables: { filter, cursor: reviews?.cursor },
        })
      }
    >
      <div className="flex w-full flex-row items-center justify-between py-2">
        <h2 className="font-medium text-lg leading-snug">{t('{{count}} Reviews', { count: reviews?.count ?? 0 })}</h2>
        {!!canReview && (
          <Button onClick={() => setReviewModal(true)} type="button">
            {t('Write a Review')}
          </Button>
        )}
      </div>
      {reviews?.entries?.map((review) => {
        return (
          <ReviewCard
            key={review.id}
            onDeleteReview={handleDeleteReview}
            onEditReview={handleEditReview}
            onReplyReview={handleRefreshData}
            placeIdOrSlug={placeIdOrSlug}
            placeOwnerId={placeOwnerId}
            profileId={profileId}
            publicationIdOrSlug={publicationIdOrSlug}
            publicationOwnerId={publicationOwnerId}
            review={review}
            vendorMarketIdOrSlug={vendorMarketIdOrSlug}
            vendorMarketItemIdOrSlug={vendorMarketItemIdOrSlug}
            vendorMarketOwnerId={vendorMarketOwnerId}
          />
        )
      })}
      <ReviewCreateEditDialog
        onOpenChange={(e) => setReviewModal(e.open)}
        onReviewChange={(data) => {
          if (onReviewChange) {
            onReviewChange(data)
          }
          queryResult.refetch()
        }}
        open={reviewModal}
        placeIdOrSlug={placeIdOrSlug}
        publicationIdOrSlug={publicationIdOrSlug}
        vendorMarketIdOrSlug={vendorMarketIdOrSlug}
        vendorMarketItemIdOrSlug={vendorMarketItemIdOrSlug}
      />
    </InfiniteScroll>
  )
}

export default Reviews
